import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CreditBalance from './CreditBalance';
import BitcoinPrice from './BitcoinPrice';
import CartButton from './CartButton';
import { useAuth } from '../contexts/AuthContext';

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [mobileSearchFocused, setMobileSearchFocused] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Use the auth context instead of direct function calls
  const { isAuthenticated, user, logout: authLogout } = useAuth();

  // Function to check if a path is active
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    try {
      await authLogout();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setMobileSearchOpen(false);
      }
    }

    function handleResize() {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
        setMobileSearchOpen(false);
      }
    }

    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSearchOpen(false);
  }, [location]);

  return (
    <header className="bg-[#120F17] py-3 sm:py-4 sticky top-0 z-50">
      <div className="container mx-auto px-3 sm:px-4 flex justify-between items-center">
        {/* Logo and Desktop Nav */}
        <div className="flex items-center gap-2 sm:gap-4 md:gap-6 lg:gap-8">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-r from-[#7F56D9] to-[#F670C7]"></div>
            <Link to="/" className="font-semibold text-base sm:text-lg truncate max-w-[100px] xs:max-w-[120px] sm:max-w-none">Developer Marketplace</Link>
          </div>
          <nav className="hidden md:flex">
            <ul className="flex gap-3 lg:gap-6 xl:gap-8">
              <li><Link to="/" className={`${isActive('/') ? 'text-[#F670C7]' : 'text-white'} hover:text-[#F670C7] transition-colors text-sm lg:text-base whitespace-nowrap`}>Home</Link></li>
              <li><Link to="/news" className={`${isActive('/news') ? 'text-white' : 'text-[var(--color-muted)]'} hover:text-white transition-colors text-sm lg:text-base whitespace-nowrap`}>News</Link></li>
              <li><Link to="/messages" className={`${isActive('/messages') ? 'text-white' : 'text-[var(--color-muted)]'} hover:text-white transition-colors text-sm lg:text-base whitespace-nowrap`}>Messages</Link></li>
              <li className="hidden lg:block"><Link to="/report" className={`${isActive('/report') ? 'text-white' : 'text-[var(--color-muted)]'} hover:text-white transition-colors text-sm lg:text-base whitespace-nowrap`}>Report</Link></li>
              <li className="hidden xl:block"><Link to="/other" className={`${isActive('/other') ? 'text-white' : 'text-[var(--color-muted)]'} hover:text-white transition-colors text-sm lg:text-base whitespace-nowrap`}>Other</Link></li>
            </ul>
          </nav>
        </div>

        {/* Right Icons Section */}
        <div className="flex items-center gap-1 xs:gap-2 sm:gap-3 lg:gap-4">
          {/* Mobile Search Toggle */}
          <button
            className="md:hidden text-[var(--color-muted)] hover:text-white transition-colors"
            onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Desktop Search Box */}
          <div ref={searchRef} className={`relative hidden md:block transition-all duration-300 ${searchFocused ? 'w-40 lg:w-60 xl:w-80' : 'w-28 lg:w-40 xl:w-60'}`}>
            <input
              type="text"
              placeholder="Search for assets"
              className="input py-1.5 sm:py-2 pl-4 pr-10 w-full bg-[#1F1A27] border border-[#2A2633] rounded-md text-white text-sm"
              style={{ paddingRight: "2.5rem" }}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  window.location.href = `/search?q=${encodeURIComponent((e.target as HTMLInputElement).value)}`;
                }
              }}
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
              <svg
                className={`transition-colors duration-300 ${searchFocused ? 'text-[#7F56D9]' : 'text-[#686868]'}`}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>

          {/* Credits Display (Desktop) */}
          <div className="hidden md:flex items-center ml-auto">
            <BitcoinPrice />
            <div className="mx-2 h-4 border-r border-[#2A2633]"></div>
            <CreditBalance variant="compact" />
            <Link to="/topup" className="ml-2 text-xs bg-[#7F56D9] hover:bg-[#6941C6] text-white px-2 py-1 rounded">
              Add Credits
            </Link>
          </div>

          {/* Messages Icon */}
          <Link to="/messages" className="relative hidden sm:block">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-[var(--color-muted)] hover:text-white transition-colors" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H6L4 18V4H20V16Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full bg-[#7F56D9] text-[10px] text-white">
              2
            </div>
          </Link>

          {/* Cart Icon */}
          <div className="hidden sm:block">
            <CartButton isActive={isActive('/cart')} />
          </div>

          {/* Notification Badge */}
          <div className="relative" ref={notificationsRef}>
            <div
              className="cursor-pointer relative"
              onClick={() => setNotificationsOpen(!notificationsOpen)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-[var(--color-muted)] hover:text-white transition-colors" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full bg-[#FF4444] text-[10px] text-white">
                4
              </div>
            </div>

            {/* Notifications Dropdown */}
            {notificationsOpen && (
              <div className="absolute right-0 mt-2 w-64 sm:w-72 lg:w-80 rounded-lg glass animate-fadeIn"
                   style={{ maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
                <div className="py-2">
                  <div className="px-4 py-2 border-b border-white/10 flex items-center justify-between">
                    <p className="text-sm font-medium text-white">Notifications</p>
                    <a href="#" className="text-xs text-[#7F56D9]">Mark all as read</a>
                  </div>

                  <div className="max-h-60 sm:max-h-80 overflow-y-auto">
                    <div className="px-4 py-3 border-b border-white/5 hover:bg-[#7F56D9]/10 transition-colors">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#7F56D9]/20 flex items-center justify-center">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-[#7F56D9]" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"/>
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-white"><span className="font-medium">Support Team</span> has responded to your ticket</p>
                          <p className="text-xs text-[var(--color-muted)] mt-1">5 minutes ago</p>
                        </div>
                      </div>
                    </div>

                    <div className="px-4 py-3 border-b border-white/5 hover:bg-[#7F56D9]/10 transition-colors">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#F670C7]/20 flex items-center justify-center">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-[#F670C7]" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"/>
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-white"><span className="font-medium">DevGenius</span> sent you a message</p>
                          <p className="text-xs text-[var(--color-muted)] mt-1">2 hours ago</p>
                        </div>
                      </div>
                    </div>

                    <div className="px-4 py-3 border-b border-white/5 hover:bg-[#7F56D9]/10 transition-colors">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-green-500" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"/>
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-white">Your order <span className="font-medium">#12345</span> has been completed</p>
                          <p className="text-xs text-[var(--color-muted)] mt-1">1 day ago</p>
                        </div>
                      </div>
                    </div>

                    <div className="px-4 py-3 hover:bg-[#7F56D9]/10 transition-colors">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-blue-500" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20.5 3L20.34 3.03L15 5.1L9 3L3.36 4.9C3.15 4.97 3 5.15 3 5.38V20.5C3 20.78 3.22 21 3.5 21L3.66 20.97L9 18.9L15 21L20.64 19.1C20.85 19.03 21 18.85 21 18.62V3.5C21 3.22 20.78 3 20.5 3ZM10 5.47L14 6.87V18.53L10 17.13V5.47Z"/>
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-white">New <span className="font-medium">product update</span> is available</p>
                          <p className="text-xs text-[var(--color-muted)] mt-1">3 days ago</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="px-4 py-2 border-t border-white/10">
                    <Link to="/account?tab=notifications" className="text-xs text-[#7F56D9] flex justify-center">
                      View all notifications
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative" ref={dropdownRef}>
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#7F56D9] to-[#F670C7] p-0.5">
                <div className="w-full h-full rounded-full bg-[#2F2C38] flex items-center justify-center overflow-hidden">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-[#A1A1A1]" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"/>
                  </svg>
                </div>
              </div>
              <span className="hidden md:block text-white text-sm">
                {user ? user.username : 'Guest'}
              </span>
            </div>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg glass animate-fadeIn z-50">
                <div className="py-1">
                  {user ? (
                    <>
                      <Link to="/account" className="block px-4 py-2 text-sm text-white hover:bg-white/10">
                        Account
                      </Link>
                      <Link to="/dashboard" className="block px-4 py-2 text-sm text-white hover:bg-white/10">
                        Dashboard
                      </Link>
                      <Link to="/orders" className="block px-4 py-2 text-sm text-white hover:bg-white/10">
                        Orders
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-white/10"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/auth" className="block px-4 py-2 text-sm text-white hover:bg-white/10">
                        Login
                      </Link>
                      <Link to="/auth" className="block px-4 py-2 text-sm text-white hover:bg-white/10" onClick={() => {
                        // Navigate to auth page with register tab active
                        // This will be handled on the auth page side
                        sessionStorage.setItem('authTab', 'register');
                      }}>
                        Register
                      </Link>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 18L18 6M6 6L18 18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 6H20M4 12H20M4 18H20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#120F17] border-t border-white/10 animate-slideDown">
          <div className="container mx-auto px-4 py-2">
            {/* Credit Balance (Mobile) */}
            <div className="mb-3 border-b border-white/10 pb-3">
              <CreditBalance />
            </div>

            <nav>
              <ul className="space-y-2">
                <li><Link to="/" className={`block py-2 ${isActive('/') ? 'text-white' : 'text-[var(--color-muted)]'}`}>Home</Link></li>
                <li><Link to="/news" className={`block py-2 ${isActive('/news') ? 'text-white' : 'text-[var(--color-muted)]'}`}>News</Link></li>
                <li><Link to="/messages" className={`block py-2 ${isActive('/messages') ? 'text-white' : 'text-[var(--color-muted)]'}`}>Messages</Link></li>
                <li>
                  <CartButton
                    variant="button"
                    isActive={isActive('/cart')}
                    className="mt-2 mb-2"
                  />
                </li>
                <li><Link to="/report" className={`block py-2 ${isActive('/report') ? 'text-white' : 'text-[var(--color-muted)]'}`}>Report</Link></li>
                <li><Link to="/other" className={`block py-2 ${isActive('/other') ? 'text-white' : 'text-[var(--color-muted)]'}`}>Other</Link></li>
                <li><Link to="/wishlist" className={`block py-2 ${isActive('/wishlist') ? 'text-white' : 'text-[var(--color-muted)]'}`}>Wishlist</Link></li>
                <li><Link to="/orders" className={`block py-2 ${isActive('/orders') ? 'text-white' : 'text-[var(--color-muted)]'}`}>Orders</Link></li>
                <li><Link to="/account" className={`block py-2 ${isActive('/account') ? 'text-white' : 'text-[var(--color-muted)]'}`}>Account</Link></li>
              </ul>
            </nav>
            <div className="mt-4 pt-4 border-t border-white/10">
              <ul className="grid grid-cols-2 gap-2">
                <li><Link to="/about" className="block py-2 text-xs text-[var(--color-muted)]">About Us</Link></li>
                <li><Link to="/contact" className="block py-2 text-xs text-[var(--color-muted)]">Contact</Link></li>
                <li><Link to="/privacy" className="block py-2 text-xs text-[var(--color-muted)]">Privacy Policy</Link></li>
                <li><Link to="/terms" className="block py-2 text-xs text-[var(--color-muted)]">Terms & Conditions</Link></li>
                <li><Link to="/cookies" className="block py-2 text-xs text-[var(--color-muted)]">Cookies Policy</Link></li>
                <li><Link to="/faq" className="block py-2 text-xs text-[var(--color-muted)]">FAQ</Link></li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Search Bar */}
      {mobileSearchOpen && (
        <div className="md:hidden border-t border-white/10 animate-slideDown">
          <div className="container mx-auto px-4 py-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for assets"
                className="input py-2 pl-4 pr-10 w-full bg-[#1F1A27] border border-[#2A2633] rounded-md text-white text-sm"
                style={{ paddingRight: "2.5rem" }}
                autoFocus
                onFocus={() => setMobileSearchFocused(true)}
                onBlur={() => setMobileSearchFocused(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    window.location.href = `/search?q=${encodeURIComponent((e.target as HTMLInputElement).value)}`;
                    setMobileSearchOpen(false);
                  }
                }}
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                <svg
                  className={`transition-colors duration-300 ${mobileSearchFocused ? 'text-[#7F56D9]' : 'text-[#686868]'}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
